import { DateTime } from 'luxon'
import { OpeningHourStandard, OpeningHours, WeekDay } from '../../types'

export const getEmptyOpeningHours = (): OpeningHours => {
  return {
    [WeekDay.Monday]: [],
    [WeekDay.Tuesday]: [],
    [WeekDay.Wednesday]: [],
    [WeekDay.Thursday]: [],
    [WeekDay.Friday]: [],
    [WeekDay.Saturday]: [],
    [WeekDay.Sunday]: [],
  }
}

/** An opening time as stored, `H:mm` or `HH:mm` - older rows and the seeds use a one-digit hour. */
const OPENING_TIME = /^([01]?\d|2[0-3]):([0-5]\d)$/

/** Minutes since midnight, or `null` for anything that is not an opening time. */
export const openingTimeToMinutes = (time: string): number | null => {
  const match = OPENING_TIME.exec(time)
  return match ? Number(match[1]) * 60 + Number(match[2]) : null
}

interface Range {
  start: number
  end: number
}

const toRange = (hour: OpeningHourStandard): Range | null => {
  const start = openingTimeToMinutes(hour.start)
  const end = openingTimeToMinutes(hour.end)
  return start === null || end === null || end <= start ? null : { start, end }
}

/**
 * How long one interval lasts, in minutes. `null` when either end is not a valid time or the
 * interval does not run forwards - an interval never crosses midnight.
 */
export const openingHourMinutes = (hour: OpeningHourStandard): number | null => {
  const range = toRange(hour)
  return range && range.end - range.start
}

/**
 * The indexes of a day's intervals that overlap another interval of the same day. Intervals that
 * only touch (`08:00–11:00`, `11:00–14:00`) do not overlap, and an interval that is not valid on its
 * own is left to its own rules rather than reported here as well.
 */
export const overlappingIndexes = (hours: readonly OpeningHourStandard[]): number[] => {
  const ranges = hours.map(toRange)
  const overlaps = (a: Range | null, b: Range | null) => !!a && !!b && a.start < b.end && a.end > b.start
  return ranges.flatMap((range, index) =>
    ranges.some((other, otherIndex) => otherIndex !== index && overlaps(range, other)) ? [index] : [],
  )
}

/**
 * A day's opening intervals, valid ones only, earliest first. `[]` is a closed day: a day missing from the stored
 * week, or present with no intervals, is closed (docs/premise-settings.md, _A day with no hours is a closed day_).
 */
export const openingIntervalsOn = (openingHours: OpeningHours, day: WeekDay): OpeningHourStandard[] =>
  (openingHours[day] ?? [])
    .map((hour) => ({ hour, range: toRange(hour) }))
    .filter((item): item is { hour: OpeningHourStandard; range: Range } => item.range !== null)
    .sort((a, b) => a.range.start - b.range.start)
    .map(({ hour }) => ({ start: hour.start, end: hour.end }))

/**
 * The weekday it is now at the premise, which is not the visitor's: near midnight a visitor in another zone is
 * already, or still, on a different day. An unknown zone falls back to UTC rather than to the browser's.
 */
export const premiseWeekday = (timezone: string, now: Date = new Date()): WeekDay => {
  const local = DateTime.fromJSDate(now, { zone: timezone })
  return (local.isValid ? local : DateTime.fromJSDate(now, { zone: 'UTC' })).weekday as WeekDay
}

/**
 * Today's opening intervals at the premise: `[]` when it is closed today, `null` when it publishes no hours at all
 * (`openingHours` is `null` publicly while `openingHoursEnabled` is off).
 */
export const todayOpeningHours = (
  openingHours: OpeningHours | null,
  timezone: string,
  now: Date = new Date(),
): OpeningHourStandard[] | null =>
  openingHours ? openingIntervalsOn(openingHours, premiseWeekday(timezone, now)) : null
